import { Typography } from "@components/atoms/Typography"
import {
  Difference,
  PlayTimeline,
  TickDate,
  Timeline,
  TimelineSelection,
  Toggle,
  ToggleContainer,
} from "@components/atoms/data/Components"
import { Container, Grid, Col } from "@components/layout/Grid"
import { cn } from "@utils/tw"
import { getColor, getQuarters, round } from "@utils/transportAffordabilityHelpers"
import { defineBlock } from "eddev/blocks"
import {
  ComponentProps,
  ComponentPropsWithoutRef,
  Dispatch,
  SetStateAction,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Chart as ChartJS, ChartOptions, registerables } from "chart.js/auto"
import { Chart } from "react-chartjs-2"
import {
  CAR_LOAN_PAYMENTS,
  FUEL,
  INSURANCE,
  PUBLIC_TRANSPORT,
  REGISTRATION_CTP_LICENSING,
  ROADSIDE_ASSIST,
  SERVICING_TYRES,
  TOLLS,
} from "@components/atoms/IconsAffordability"
import * as IconsMap from "@components/atoms/IconsMap"
import { numberWithCommas } from "@utils/numberWithCommas"
import { useBreakpointIsBelow, useIsMobile } from "@hooks/useViewportDerived"
import {
  calculateAngle,
  generateArrayWithIntervals,
  getAffordabilityColor,
  getArrayPosition,
} from "@utils/chartjsUtils"
import { useMethodologyTooltip, useTransportData } from "@hooks/queries"
import { useHydrating } from "eddev/routing"
import { GridReset } from "@components/layout/GridReset"
import { AnimatePresence, motion, inView } from "framer-motion"
import { CLOSE, INFO } from "@components/atoms/Icons"
import { Button } from "@components/atoms/Button"
import { Tooltip } from "react-tooltip"

export const meta: BlockMeta = {
  title: "Transport Affordability Header",
  category: "common",
  icon: "dashboard",
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post", "data-embed"],
    multiple: false,
  },
}

ChartJS.register(...registerables)

type DataDetailT = {
  type: string
  cost: number
  quarter: string
}

type ChartDataT = {
  location: string
  state: string
  taxonomy: "City" | "Regional"
  data: DataDetailT[][]
}

const iconMap = {
  "Car loan Payments": CAR_LOAN_PAYMENTS,
  "Registration, CTP & Licensing": REGISTRATION_CTP_LICENSING,
  Insurance: INSURANCE,
  "Servicing & Tyres": SERVICING_TYRES,
  Fuel: FUEL,
  "Public Transport": PUBLIC_TRANSPORT,
  Tolls: TOLLS,
  "Roadside Assist": ROADSIDE_ASSIST,
}

export default defineBlock("data/transport-affordability-header", (props) => {
  const taTooltip = useMethodologyTooltip(undefined)
  const transportAffordabilityData = useTransportData(undefined)
  const hydrating = useHydrating()
  const [timeframe, setTimeframe] = useState<"weekly" | "yearly">("weekly")
  const [currentPeriod, setCurrentPeriod] = useState<number>(0)
  const [play, setPlay] = useState<boolean>(false)
  const [quarters, setQuarters] = useState<string[] | null>(null)
  const [currentLocation, setCurrentLocation] = useState<{
    state: string
    location: string
  }>({
    state: "National",
    location: "National Average",
  })
  const [hover, setHover] = useState<number | null>(null)

  const isMobile = useIsMobile()
  const isTablet = useBreakpointIsBelow("laptop")

  const rawChartData: ChartDataT[] = useMemo(() => {
    const sortedData: ChartDataT[] = []

    if (transportAffordabilityData.data?.transportAffordabilityData) {
      const datasets = transportAffordabilityData.data.transportAffordabilityData.map((data) =>
        JSON.parse(data?.data ?? ""),
      )
      const sortedQuarters = getQuarters(datasets)
      setQuarters(sortedQuarters)
      setCurrentPeriod(sortedQuarters.length - 1)

      const locations: string[] = []
      datasets.forEach((dataset) => {
        if (!locations.includes(dataset["Location"])) {
          locations.push(dataset["Location"])
        }
      })

      locations.forEach((location) => {
        const dataByLocation = datasets.filter((dataset) => dataset["Location"] === location)
        const dataset: ChartDataT = {
          location: location,
          state: dataByLocation[0]["State"],
          taxonomy: dataByLocation[0]["City or Regional"],
          data: sortedQuarters.map((quarter) => {
            const locationData: DataDetailT[] = []
            dataByLocation.forEach((data, i) => {
              locationData.push({
                type: data["Field"],
                cost: parseFloat(dataByLocation[i][quarter]?.replace("$", "")),
                quarter: quarter,
              })
            })
            return locationData
          }),
        }

        sortedData.push(dataset)
      })
    }

    return sortedData
  }, [transportAffordabilityData.data?.transportAffordabilityData])

  const chart1Ref = useRef<ChartJS>(null)

  const chart2Ref = useRef<ChartJS>(null)

  function chart1Animate() {
    if (chart1Ref.current && chart1Data) {
      var canvas = chart1Ref.current.canvas
      var ctx = canvas.getContext("2d")
      var cw = canvas.offsetWidth
      var ch = canvas.offsetHeight
      var cx = cw / 2
      var cy = ch / 2

      const zero = (-202.5 * Math.PI) / 180 // Zero point in radians
      let maxValue = 0
      rawChartData.forEach((data) => {
        data.data.forEach((dataset) => {
          const costItem = dataset.filter((set) => set.type === "Share of Income")[0]

          if (costItem && costItem.cost > maxValue) {
            maxValue = costItem.cost
          }
        })
      })
      maxValue = maxValue * (360 / 225)
      const rangeRadians = (360 * Math.PI) / 180 // Range of the chart in radian
      const sumValue = chart1Data.datasets[0].data.slice(0, -2).reduce((a, b) => a + b, 0)
      const angleInRadians = calculateAngle(sumValue, zero, maxValue, rangeRadians)
      const intervals = generateArrayWithIntervals(
        chart1Data.datasets[0].data.slice(0, -1).reduce((a, b) => a + b, 0),
        2,
      )

      if (ctx) {
        ctx.translate(cx, cy)
        ctx.rotate(angleInRadians)
        ctx.beginPath()
        ctx.moveTo(0, -8)
        ctx.lineTo(cx - 20, 0)
        ctx.lineTo(0, 8)
        ctx.fillStyle = "#fff"
        ctx.fill()
        ctx.rotate(-angleInRadians)
        ctx.translate(-cx, -cy)
        ctx.beginPath()
        ctx.arc(cx, cy, 7, 0, Math.PI * 2)
        ctx.fill()

        ctx.fillStyle = "#222"
        ctx.beginPath()
        ctx.arc(cx, cy, cx / 2, 0, (360 * Math.PI) / 180)
        ctx.fill()

        ctx.translate(cx, cy)
        ctx.strokeStyle = "#222"
        ctx.lineWidth = 1.2
        intervals.slice(0, -1).forEach(function (value, index, arr) {
          if (ctx) {
            let angle = calculateAngle(value, zero, maxValue, rangeRadians)
            ctx.rotate(angle)
            ctx.beginPath()
            ctx.moveTo(isMobile ? cx - 10 : cx - 25, 0)
            ctx.lineTo(cx, 0)
            ctx.stroke()

            let textX = isMobile ? cx - 30 : cx - 45
            let textY = 0
            ctx.save()

            ctx.translate(textX, textY)
            ctx.rotate(-angle)

            ctx.font = "bold 12px Manrope"
            ctx.fillStyle = "#222"
            ctx.textAlign = "center"
            ctx.textBaseline = "middle"
            ctx.fillText(value.toString(), 0, 0)

            ctx.restore()
            ctx.rotate(-angle)
          }
        })

        // Add centre text
        ctx.translate(0, -40)
        ctx.font = "bold 40px Manrope"
        ctx.fillStyle = "white"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(`${round(sumValue, 1)}%`, 0, 0)
        ctx.translate(0, 40)

        ctx.font = "bold 12px Manrope"
        ctx.fillStyle = "white"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(`% of income*`, 0, 0)
        ctx.translate(0, 18)
        ctx.fillText(`spent on transport costs`, 0, 0)
        ctx.translate(0, -18)

        // Difference
        if (currentPeriod !== 0) {
          const neg = (sumValue - chart1Data.datasets[0].previousValue!).toString().includes("-") ? true : false
          ctx.translate(-60, 50)
          ctx.beginPath()
          ctx.arc(0, 0, 12, 0, 2 * Math.PI, false)
          ctx.fillStyle = neg ? "#CCE7E0" : "#EDCDCD"
          ctx.fill()

          ctx.font = "bold 12px Manrope"
          ctx.fillStyle = neg ? "#008666" : "#CA372C"
          ctx.textAlign = "center"
          ctx.textBaseline = "middle"
          ctx.fillText(neg ? `↓` : `↑`, 0, 0)

          ctx.translate(60, -50)

          ctx.translate(15, 50)
          ctx.font = "12px Manrope"
          ctx.fillStyle = "#8E8E8E"
          ctx.textAlign = "center"
          ctx.textBaseline = "middle"
          ctx.fillText(
            `${(sumValue - chart1Data.datasets[0].previousValue!).toFixed(1).replace("-", "")}% since ${chart1Data!.datasets[0].previousQuarter}`,
            0,
            0,
          )
          ctx.translate(-15, -50)
        }

        ctx.translate(-cx, -cy)
      }
    }
  }

  const chart1Options: ChartOptions<"doughnut"> = {
    cutout: "70%",
    rotation: -112.5,
    circumference: 360,

    events: [],

    animation: {
      onProgress: chart1Animate,
      onComplete: chart1Animate,
    },

    plugins: {
      legend: {
        display: false,
      },
    },
  }

  const [nationalAverage, setNationalAverage] = useState<number>()
  const [prevNationalAverage, setPrevNationalAverage] = useState<number>()
  function chart2Animate() {
    if (chart2Ref.current && chart2Data && nationalAverage) {
      var canvas = chart2Ref.current.canvas
      var ctx = canvas.getContext("2d")
      var cw = canvas.offsetWidth
      var ch = canvas.offsetHeight
      var cx = cw / 2
      var cy = ch / 2

      const zero = (-202.5 * Math.PI) / 180 // Zero point in radians
      const maxValue = chart2Data.datasets[0].data.reduce((a, b) => a + b, 0) // Maximum value in the dataset
      const rangeRadians = (360 * Math.PI) / 180 // Range of the chart in radian
      const sumValue =
        currentLocation.location === "National Average"
          ? nationalAverage
          : chart2Data.datasets[0].data.slice(0, -2).reduce((a, b) => a + b, 0)
      const angleInRadians = calculateAngle(sumValue, zero, maxValue, rangeRadians)
      const intervals = generateArrayWithIntervals(
        chart2Data.datasets[0].data.slice(0, -1).reduce((a, b) => a + b, 0),
        50,
      )

      if (ctx) {
        ctx.translate(cx, cy)
        ctx.rotate(angleInRadians)
        ctx.beginPath()
        ctx.moveTo(0, -8)
        ctx.lineTo(cx - 20, 0)
        ctx.lineTo(0, 8)
        ctx.fillStyle = "#fff"
        ctx.fill()
        ctx.rotate(-angleInRadians)
        ctx.translate(-cx, -cy)
        ctx.beginPath()
        ctx.arc(cx, cy, 7, 0, Math.PI * 2)
        ctx.fill()

        ctx.fillStyle = "#222"
        ctx.beginPath()
        ctx.arc(cx, cy, cx / 2, 0, (360 * Math.PI) / 180)
        ctx.fill()

        ctx.translate(cx, cy)
        ctx.strokeStyle = "#222"
        ctx.lineWidth = 1.2
        intervals.slice(0, -1).forEach(function (value, index, arr) {
          if (ctx) {
            let angle = calculateAngle(value, zero, maxValue, rangeRadians)
            ctx.rotate(angle)
            ctx.beginPath()
            ctx.moveTo(isMobile ? cx - 10 : cx - 25, 0)
            ctx.lineTo(cx, 0)
            ctx.stroke()

            let textX = isMobile ? cx - 30 : cx - 45
            let textY = 0
            ctx.save()

            ctx.translate(textX, textY)
            ctx.rotate(-angle)

            ctx.font = "bold 12px Manrope"
            ctx.fillStyle = "#222"
            ctx.textAlign = "center"
            ctx.textBaseline = "middle"
            ctx.fillText(`$${value}`, 0, 0)

            ctx.restore()
            ctx.rotate(-angle)
          }
        })

        // Add centre text
        ctx.translate(0, -40)
        ctx.font = "bold 40px Manrope"
        ctx.fillStyle = "white"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        if (timeframe === "yearly") {
          ctx.fillText(`$${numberWithCommas(parseFloat((sumValue * 52).toFixed(0)))}`, 0, 0)
        } else {
          ctx.fillText(`$${sumValue.toFixed(2)}`, 0, 0)
        }
        ctx.translate(0, 40)

        ctx.font = "bold 12px Manrope"
        ctx.fillStyle = "white"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        if (timeframe === "yearly") {
          ctx.fillText(`Transport costs per year`, 0, 0)
        } else {
          ctx.fillText(`Transport costs per week`, 0, 0)
        }

        // Difference
        if (currentPeriod !== 0 && prevNationalAverage) {
          const prevValue =
            currentLocation.location === "National Average"
              ? prevNationalAverage
              : chart2Data.datasets[0].previousValue!
          const neg = (sumValue - prevValue).toString().includes("-") ? true : false
          ctx.translate(-60, 50)
          ctx.beginPath()
          ctx.arc(0, 0, 12, 0, 2 * Math.PI, false)
          ctx.fillStyle = neg ? "#CCE7E0" : "#EDCDCD"
          ctx.fill()

          ctx.font = "bold 12px Manrope"
          ctx.fillStyle = neg ? "#008666" : "#CA372C"
          ctx.textAlign = "center"
          ctx.textBaseline = "middle"
          ctx.fillText(neg ? `↓` : `↑`, 0, 0)

          ctx.translate(60, -50)

          ctx.translate(15, 50)
          ctx.font = "12px Manrope"
          ctx.fillStyle = "#8E8E8E"
          ctx.textAlign = "center"
          ctx.textBaseline = "middle"

          if (timeframe === "yearly") {
            ctx.fillText(
              `$${numberWithCommas(parseFloat(((sumValue - chart2Data.datasets[0].previousValue!) * 52).toFixed().replace("-", "")))} since ${chart2Data!.datasets[0].previousQuarter}`,
              0,
              0,
            )
          } else {
            ctx.fillText(
              `$${(sumValue - chart2Data.datasets[0].previousValue!).toFixed(2).replace("-", "")} since ${chart2Data!.datasets[0].previousQuarter}`,
              0,
              0,
            )
          }
          ctx.translate(-15, -50)
        }

        ctx.translate(-cx, -cy)
      }
    }
  }

  const chart2Options: ChartOptions<"doughnut"> = {
    cutout: "70%",
    rotation: -112.5,
    circumference: 360,

    events: [],

    animation: {
      onProgress: chart2Animate,
      onComplete: chart2Animate,
    },

    plugins: {
      legend: {
        display: false,
      },
    },
  }

  const chart1Data = useMemo(() => {
    if (rawChartData) {
      let highestCost = 0
      let value = 0
      let previousValue = null
      let previousQuarter = null

      rawChartData.forEach((data) => {
        data.data.forEach((dataset) => {
          const costItem = dataset.filter((set) => set.type === "Share of Income")[0]

          if (costItem && costItem.cost > highestCost) {
            highestCost = costItem.cost
          }
        })
      })

      if (currentLocation.location === "National Average") {
        const averages: number[] = []
        const previousAverages: number[] = []
        rawChartData.forEach((data) => {
          const average = data.data[currentPeriod].filter((dataset) => dataset.type === "Share of Income")[0].cost
          const previousAverage =
            currentPeriod === 0
              ? null
              : data.data[currentPeriod - 1].filter((dataset) => dataset.type === "Share of Income")[0].cost
          if (average !== 0) {
            averages.push(average)
            if (previousAverage) previousAverages.push(previousAverage)
            previousQuarter = currentPeriod === 0 ? null : data.data[currentPeriod - 1][0].quarter
          }
        })
        value = averages.reduce((a, b) => a + b, 0) / averages.length
        previousValue =
          currentPeriod === 0 ? null : previousAverages.reduce((a, b) => a + b, 0) / previousAverages.length
      } else {
        const locationData = rawChartData.filter((data) => data.location === currentLocation.location)[0].data
        value = locationData[currentPeriod].filter((dataset) => dataset.type === "Share of Income")[0].cost
        previousValue =
          currentPeriod === 0
            ? null
            : locationData[currentPeriod - 1].filter((dataset) => dataset.type === "Share of Income")[0].cost
        previousQuarter = currentPeriod === 0 ? null : locationData[currentPeriod - 1][0].quarter
      }

      return {
        labels: ["% of income* spent on transport costs"],
        datasets: [
          {
            label: currentLocation.location,
            data: [
              parseFloat(value.toFixed(2)),
              parseFloat((highestCost - value).toFixed(2)),
              parseFloat(((360 / 225) * highestCost - highestCost).toFixed(2)),
            ],
            backgroundColor: ["#5E5E5E", "#5E5E5E40", "#222222"],
            borderWidth: 0,
            previousValue: previousValue ? parseFloat(previousValue.toFixed(2)) : null,
            previousQuarter: previousQuarter,
          },
        ],
      }
    }
  }, [currentPeriod, rawChartData, currentLocation])

  const chart2Data = useMemo(() => {
    if (rawChartData) {
      let previousQuarter = null
      let previousValue = null

      const loan: number[] = []
      const rego: number[] = []
      const insu: number[] = []
      const serv: number[] = []
      const fuel: number[] = []
      const tran: number[] = []
      const toll: number[] = []
      const asst: number[] = []

      const prevloan: number[] = []
      const prevrego: number[] = []
      const previnsu: number[] = []
      const prevserv: number[] = []
      const prevfuel: number[] = []
      const prevtran: number[] = []
      const prevtoll: number[] = []
      const prevasst: number[] = []

      const regionAverage: number[] = []
      const regionPrevAverage: number[] = []

      if (currentLocation.location === "National Average") {
        rawChartData.forEach((data) => {
          const loanav = data.data[currentPeriod].filter((dataset) => dataset.type === "Car loan Payments")[0].cost
          const regoav = data.data[currentPeriod].filter(
            (dataset) => dataset.type === "Registration, CTP & Licensing",
          )[0].cost
          const insuav = data.data[currentPeriod].filter((dataset) => dataset.type === "Insurance")[0].cost
          const servav = data.data[currentPeriod].filter((dataset) => dataset.type === "Servicing & Tyres")[0].cost
          const fuelav = data.data[currentPeriod].filter((dataset) => dataset.type === "Fuel")[0].cost
          const tranav = data.data[currentPeriod].filter((dataset) => dataset.type === "Public Transport")[0].cost
          const tollav = data.data[currentPeriod].filter((dataset) => dataset.type === "Tolls")[0].cost
          const asstav = data.data[currentPeriod].filter((dataset) => dataset.type === "Roadside Assist")[0].cost

          if (
            loanav === 0 &&
            regoav === 0 &&
            insuav === 0 &&
            servav === 0 &&
            fuelav === 0 &&
            tranav === 0 &&
            tollav === 0 &&
            asstav === 0
          ) {
          } else {
            loan.push(loanav)
            rego.push(regoav)
            insu.push(insuav)
            serv.push(servav)
            fuel.push(fuelav)
            tran.push(tranav)
            toll.push(tollav)
            asst.push(asstav)
          }

          regionAverage.push(
            parseFloat((loanav + regoav + insuav + servav + fuelav + tranav + tollav + asstav).toFixed(2)),
          )
          if (currentPeriod !== 0) {
            const prevloanav = data.data[currentPeriod - 1].filter((dataset) => dataset.type === "Car loan Payments")[0]
              .cost
            const prevregoav = data.data[currentPeriod - 1].filter(
              (dataset) => dataset.type === "Registration, CTP & Licensing",
            )[0].cost
            const previnsuav = data.data[currentPeriod - 1].filter((dataset) => dataset.type === "Insurance")[0].cost
            const prevservav = data.data[currentPeriod - 1].filter((dataset) => dataset.type === "Servicing & Tyres")[0]
              .cost
            const prevfuelav = data.data[currentPeriod - 1].filter((dataset) => dataset.type === "Fuel")[0].cost
            const prevtranav = data.data[currentPeriod - 1].filter((dataset) => dataset.type === "Public Transport")[0]
              .cost
            const prevtollav = data.data[currentPeriod - 1].filter((dataset) => dataset.type === "Tolls")[0].cost
            const prevasstav = data.data[currentPeriod - 1].filter((dataset) => dataset.type === "Roadside Assist")[0]
              .cost

            if (
              prevloanav === 0 &&
              prevregoav === 0 &&
              previnsuav === 0 &&
              prevservav === 0 &&
              prevfuelav === 0 &&
              prevtranav === 0 &&
              prevtollav === 0 &&
              prevasstav === 0
            ) {
            } else {
              prevloan.push(prevloanav)
              prevrego.push(prevregoav)
              previnsu.push(previnsuav)
              prevserv.push(prevservav)
              prevfuel.push(prevfuelav)
              prevtran.push(prevtranav)
              prevtoll.push(prevtollav)
              prevasst.push(prevasstav)
            }

            regionPrevAverage.push(
              parseFloat(
                (
                  prevloanav +
                  prevregoav +
                  previnsuav +
                  prevservav +
                  prevfuelav +
                  prevtranav +
                  prevtollav +
                  prevasstav
                ).toFixed(2),
              ),
            )
          }

          previousQuarter = currentPeriod === 0 ? null : data.data[currentPeriod - 1][0].quarter

          const totalLocations = currentPeriod < 5 ? 8 : 15
          setNationalAverage(Math.round((regionAverage.reduce((a, b) => a + b, 0) / totalLocations) * 100) / 100) // Current National Average
          setPrevNationalAverage(
            Math.round((regionPrevAverage.reduce((a, b) => a + b, 0) / totalLocations) * 100) / 100,
          ) // Previous National Average
        })
      } else {
        const locationData = rawChartData.filter((data) => data.location === currentLocation.location)[0].data
        const loanav = locationData[currentPeriod].filter((dataset) => dataset.type === "Car loan Payments")[0].cost
        const regoav = locationData[currentPeriod].filter(
          (dataset) => dataset.type === "Registration, CTP & Licensing",
        )[0].cost
        const insuav = locationData[currentPeriod].filter((dataset) => dataset.type === "Insurance")[0].cost
        const servav = locationData[currentPeriod].filter((dataset) => dataset.type === "Servicing & Tyres")[0].cost
        const fuelav = locationData[currentPeriod].filter((dataset) => dataset.type === "Fuel")[0].cost
        const tranav = locationData[currentPeriod].filter((dataset) => dataset.type === "Public Transport")[0].cost
        const tollav = locationData[currentPeriod].filter((dataset) => dataset.type === "Tolls")[0].cost
        const asstav = locationData[currentPeriod].filter((dataset) => dataset.type === "Roadside Assist")[0].cost

        loan.push(loanav)
        rego.push(regoav)
        insu.push(insuav)
        serv.push(servav)
        fuel.push(fuelav)
        tran.push(tranav)
        toll.push(tollav)
        asst.push(asstav)

        if (currentPeriod !== 0) {
          const prevloanav = locationData[currentPeriod - 1].filter(
            (dataset) => dataset.type === "Car loan Payments",
          )[0].cost
          const prevregoav = locationData[currentPeriod - 1].filter(
            (dataset) => dataset.type === "Registration, CTP & Licensing",
          )[0].cost
          const previnsuav = locationData[currentPeriod - 1].filter((dataset) => dataset.type === "Insurance")[0].cost
          const prevservav = locationData[currentPeriod - 1].filter(
            (dataset) => dataset.type === "Servicing & Tyres",
          )[0].cost
          const prevfuelav = locationData[currentPeriod - 1].filter((dataset) => dataset.type === "Fuel")[0].cost
          const prevtranav = locationData[currentPeriod - 1].filter((dataset) => dataset.type === "Public Transport")[0]
            .cost
          const prevtollav = locationData[currentPeriod - 1].filter((dataset) => dataset.type === "Tolls")[0].cost
          const prevasstav = locationData[currentPeriod - 1].filter((dataset) => dataset.type === "Roadside Assist")[0]
            .cost

          prevloan.push(prevloanav)
          prevrego.push(prevregoav)
          previnsu.push(previnsuav)
          prevserv.push(prevservav)
          prevfuel.push(prevfuelav)
          prevtran.push(prevtranav)
          prevtoll.push(prevtollav)
          prevasst.push(prevasstav)
        }

        previousQuarter = currentPeriod === 0 ? null : locationData[currentPeriod - 1][0].quarter
      }

      let dataset: { value: number; name: string }[] = []
      let prevdataset: { value: number; name: string }[] = []

      if (loan.length > 0) {
        dataset = [
          {
            value: loan.reduce((a, b) => a + b) / loan.length,
            name: "Car loan Payments",
          },
          {
            value: rego.reduce((a, b) => a + b, 0) / rego.length,
            name: "Registration, CTP & Licensing",
          },
          {
            value: insu.reduce((a, b) => a + b, 0) / insu.length,
            name: "Insurance",
          },
          {
            value: serv.reduce((a, b) => a + b, 0) / serv.length,
            name: "Servicing & Tyres",
          },
          {
            value: fuel.reduce((a, b) => a + b, 0) / fuel.length,
            name: "Fuel",
          },
          {
            value: tran.reduce((a, b) => a + b, 0) / tran.length,
            name: "Public Transport",
          },
          {
            value: toll.reduce((a, b) => a + b, 0) / toll.length,
            name: "Tolls",
          },
          {
            value: asst.reduce((a, b) => a + b, 0) / asst.length,
            name: "Roadside Assist",
          },
        ]

        prevdataset =
          currentPeriod === 0
            ? []
            : [
                {
                  value: prevloan.reduce((a, b) => a + b) / prevloan.length,
                  name: "Car loan Payments",
                },
                {
                  value: prevrego.reduce((a, b) => a + b, 0) / prevrego.length,
                  name: "Registration, CTP & Licensing",
                },
                {
                  value: previnsu.reduce((a, b) => a + b, 0) / previnsu.length,
                  name: "Insurance",
                },
                {
                  value: prevserv.reduce((a, b) => a + b, 0) / prevserv.length,
                  name: "Servicing & Tyres",
                },
                {
                  value: prevfuel.reduce((a, b) => a + b, 0) / prevfuel.length,
                  name: "Fuel",
                },
                {
                  value: prevtran.reduce((a, b) => a + b, 0) / prevtran.length,
                  name: "Public Transport",
                },
                {
                  value: prevtoll.reduce((a, b) => a + b, 0) / prevtoll.length,
                  name: "Tolls",
                },
                {
                  value: prevasst.reduce((a, b) => a + b, 0) / prevasst.length,
                  name: "Roadside Assist",
                },
              ]

        dataset.sort((a, b) => a.value - b.value)
      }

      let highestCost = 0
      rawChartData.forEach((data) => {
        data.data.forEach((dataset) => {
          const costItem = dataset.filter((set) => set.type !== "Share of Income")
          const totalCost = costItem.map((item) => item.cost).reduce((a, b) => a + b, 0)

          if (totalCost > highestCost) {
            highestCost = totalCost
          }
        })
      })

      const total = dataset?.map((data) => data?.value)?.reduce((a, b) => a + b, 0) ?? 0

      return {
        labels: ["Transport costs per week"],
        datasets: [
          {
            label: currentLocation.location,
            data: dataset
              .map((data) => data.value)
              .concat([
                parseFloat((highestCost - total).toFixed(2)),
                parseFloat(((360 / 225) * highestCost - highestCost).toFixed(2)),
              ]),
            backgroundColor: [
              getAffordabilityColor(0, false, hover ? (hover !== 8 ? true : false) : false),
              getAffordabilityColor(1, false, hover ? (hover !== 7 ? true : false) : false),
              getAffordabilityColor(2, false, hover ? (hover !== 6 ? true : false) : false),
              getAffordabilityColor(3, false, hover ? (hover !== 5 ? true : false) : false),
              getAffordabilityColor(4, false, hover ? (hover !== 4 ? true : false) : false),
              getAffordabilityColor(5, false, hover ? (hover !== 3 ? true : false) : false),
              getAffordabilityColor(6, false, hover ? (hover !== 2 ? true : false) : false),
              getAffordabilityColor(7, false, hover ? (hover !== 1 ? true : false) : false),
              "#5E5E5E40",
              "#222222",
            ],
            borderWidth: 0,
            order: dataset.sort((a, b) => b.value - a.value).map((data) => data.name),
            previousValue: prevdataset
              ? parseFloat(
                  prevdataset
                    .map((data) => data.value)
                    .reduce((a, b) => a + b, 0)
                    .toFixed(2),
                )
              : null,
            previousQuarter: previousQuarter,
          },
        ],
      }
    }

    return null
  }, [currentPeriod, rawChartData, currentLocation, hover])

  useEffect(() => {
    if (play && rawChartData) {
      const interval = setInterval(() => {
        if (currentPeriod === rawChartData[0].data.length - 1) {
          setPlay(false)
        } else {
          setCurrentPeriod(currentPeriod + 1)
        }
      }, 500)

      return () => clearInterval(interval)
    }
  }, [play, currentPeriod])

  const [sidebar, setSidebar] = useState<{
    active: boolean
    type?: keyof typeof iconMap
  }>({
    active: false,
  })

  return (
    <>
      <HeaderContainer>
        <div className="grid-auto px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] gap-6">
          <div className="col-span-12 flex flex-col items-center z-[1] relative lg:absolute lg:left-1/2 lg:-translate-x-1/2">
            <StyledToggleContainer>
              <Toggle>
                <button className={timeframe === "weekly" ? "active" : ""} onClick={() => setTimeframe("weekly")}>
                  Weekly
                </button>
                <button className={timeframe === "yearly" ? "active" : ""} onClick={() => setTimeframe("yearly")}>
                  Yearly
                </button>
              </Toggle>
            </StyledToggleContainer>

            <Selector>
              <Typography as="span" variant="caps" className="whitespace-nowrap">
                Location
              </Typography>
              <select
                onChange={(e) => {
                  if (e.currentTarget.value === "National Average") {
                    setCurrentLocation({
                      state: "National",
                      location: "National Average",
                    })
                  } else {
                    const state = rawChartData.filter((data) => data.location === e.currentTarget.value)[0].state
                    setCurrentLocation({
                      state: state,
                      location: e.currentTarget.value,
                    })
                  }
                }}
              >
                <option value="National Average">National Average</option>
                <option value="Sydney">Sydney</option>
                <option value="Melbourne">Melbourne</option>
                <option value="Brisbane">Brisbane</option>
                <option value="Adelaide">Adelaide</option>
                <option value="Perth">Perth</option>
                <option value="Hobart">Hobart</option>
                <option value="Darwin">Darwin</option>
                <option value="Canberra">Canberra</option>
                <option value="Wagga Wagga">Wagga Wagga</option>
                <option value="Geelong">Geelong</option>
                <option value="Townsville">Townsville</option>
                <option value="Mount Gambier">Mount Gambier</option>
                <option value="Bunbury">Bunbury</option>
                <option value="Launceston">Launceston</option>
                <option value="Alice Springs">Alice Springs</option>
              </select>

              <MapIcon
                style={{
                  color: getColor(
                    currentLocation.state,
                    currentLocation.location === "National Average"
                      ? false
                      : rawChartData.filter((data) => data.location === currentLocation.location)[0].taxonomy ===
                          "Regional"
                        ? true
                        : false,
                  ),
                }}
              >
                {
                  IconsMap[
                    (currentLocation.location === "National Average"
                      ? "NATIONAL"
                      : currentLocation.state.toUpperCase()) as keyof typeof IconsMap
                  ]
                }
              </MapIcon>
            </Selector>
          </div>

          <div className="hidden md:flex col-span-2 lg:col-span-1" />

          <div className="hidden lg:flex items-center col-span-5">
            {chart1Data && (
              <ChartInstance>
                <Chart
                  type="doughnut"
                  // @ts-ignore
                  ref={chart1Ref}
                  data={chart1Data}
                  options={chart1Options}
                />
              </ChartInstance>
            )}
          </div>

          <div className="col-span-12 md:col-span-8 lg:col-span-5 flex items-center">
            {chart2Data && (
              <ChartInstance>
                <Chart
                  type="doughnut"
                  // @ts-ignore
                  ref={chart2Ref}
                  // @ts-ignore
                  data={chart2Data}
                  options={chart2Options}
                />
              </ChartInstance>
            )}
          </div>

          <div className="col-span-12 md:col-span-2 lg:col-span-1 relative right-auto md:right-[calc((100vw-var(--tw-grid-inner-width))*(1/2))] lg:right-auto">
            <Typography as="span" variant="caps" className="whitespace-nowrap md:float-right">
              Categories
            </Typography>

            {chart2Data && (
              <Scroll>
                <CategoriesContainer>
                  <CategoryButton
                    onClick={() =>
                      setSidebar({
                        active: true,
                        type: "Car loan Payments",
                      })
                    }
                    style={{
                      left: isMobile ? `${getArrayPosition(chart2Data, "Car loan Payments") * 50}px` : "auto",
                      top: isMobile ? "auto" : `${getArrayPosition(chart2Data, "Car loan Payments") * 50}px`,
                      background: getAffordabilityColor(getArrayPosition(chart2Data, "Car loan Payments"), true),
                    }}
                    onMouseOver={() => setHover(getArrayPosition(chart2Data, "Car loan Payments") + 1)}
                    onMouseOut={() => setHover(null)}
                  >
                    {CAR_LOAN_PAYMENTS}

                    <HoverState>
                      <Typography as="span" variant="label-s">
                        Car loan Payments
                      </Typography>
                      <Typography as="span" variant="text-s">
                        $
                        {chart2Data.datasets[0].data[
                          chart2Data.datasets[0].data.length - 3 - getArrayPosition(chart2Data, "Car loan Payments")
                        ].toFixed(2)}
                      </Typography>
                    </HoverState>
                  </CategoryButton>
                  <CategoryButton
                    onClick={() =>
                      setSidebar({
                        active: true,
                        type: "Registration, CTP & Licensing",
                      })
                    }
                    style={{
                      left: isMobile
                        ? `${getArrayPosition(chart2Data, "Registration, CTP & Licensing") * 50}px`
                        : "auto",
                      top: isMobile
                        ? "auto"
                        : `${getArrayPosition(chart2Data, "Registration, CTP & Licensing") * 50}px`,
                      background: getAffordabilityColor(
                        getArrayPosition(chart2Data, "Registration, CTP & Licensing"),
                        true,
                      ),
                    }}
                    onMouseOver={() => setHover(getArrayPosition(chart2Data, "Registration, CTP & Licensing") + 1)}
                    onMouseOut={() => setHover(null)}
                  >
                    {REGISTRATION_CTP_LICENSING}

                    <HoverState>
                      <Typography as="span" variant="label-s">
                        Registration, CTP & Licensing
                      </Typography>
                      <Typography as="span" variant="text-s">
                        $
                        {chart2Data.datasets[0].data[
                          chart2Data.datasets[0].data.length -
                            3 -
                            getArrayPosition(chart2Data, "Registration, CTP & Licensing")
                        ].toFixed(2)}
                      </Typography>
                    </HoverState>
                  </CategoryButton>
                  <CategoryButton
                    onClick={() =>
                      setSidebar({
                        active: true,
                        type: "Insurance",
                      })
                    }
                    style={{
                      left: isMobile ? `${getArrayPosition(chart2Data, "Insurance") * 50}px` : "auto",
                      top: isMobile ? "auto" : `${getArrayPosition(chart2Data, "Insurance") * 50}px`,
                      background: getAffordabilityColor(getArrayPosition(chart2Data, "Insurance"), true),
                    }}
                    onMouseOver={() => setHover(getArrayPosition(chart2Data, "Insurance") + 1)}
                    onMouseOut={() => setHover(null)}
                  >
                    {INSURANCE}

                    <HoverState>
                      <Typography as="span" variant="label-s">
                        Insurance
                      </Typography>
                      <Typography as="span" variant="text-s">
                        $
                        {chart2Data.datasets[0].data[
                          chart2Data.datasets[0].data.length - 3 - getArrayPosition(chart2Data, "Insurance")
                        ].toFixed(2)}
                      </Typography>
                    </HoverState>
                  </CategoryButton>
                  <CategoryButton
                    onClick={() =>
                      setSidebar({
                        active: true,
                        type: "Servicing & Tyres",
                      })
                    }
                    style={{
                      left: isMobile ? `${getArrayPosition(chart2Data, "Servicing & Tyres") * 50}px` : "auto",
                      top: isMobile ? "auto" : `${getArrayPosition(chart2Data, "Servicing & Tyres") * 50}px`,
                      background: getAffordabilityColor(getArrayPosition(chart2Data, "Servicing & Tyres"), true),
                    }}
                    onMouseOver={() => setHover(getArrayPosition(chart2Data, "Servicing & Tyres") + 1)}
                    onMouseOut={() => setHover(null)}
                  >
                    {SERVICING_TYRES}

                    <HoverState>
                      <Typography as="span" variant="label-s">
                        Servicing & Tyres
                      </Typography>
                      <Typography as="span" variant="text-s">
                        $
                        {chart2Data.datasets[0].data[
                          chart2Data.datasets[0].data.length - 3 - getArrayPosition(chart2Data, "Servicing & Tyres")
                        ].toFixed(2)}
                      </Typography>
                    </HoverState>
                  </CategoryButton>
                  <CategoryButton
                    onClick={() =>
                      setSidebar({
                        active: true,
                        type: "Fuel",
                      })
                    }
                    style={{
                      left: isMobile ? `${getArrayPosition(chart2Data, "Fuel") * 50}px` : "auto",
                      top: isMobile ? "auto" : `${getArrayPosition(chart2Data, "Fuel") * 50}px`,
                      background: getAffordabilityColor(getArrayPosition(chart2Data, "Fuel"), true),
                    }}
                    onMouseOver={() => setHover(getArrayPosition(chart2Data, "Fuel") + 1)}
                    onMouseOut={() => setHover(null)}
                  >
                    {FUEL}

                    <HoverState>
                      <Typography as="span" variant="label-s">
                        Fuel
                      </Typography>
                      <Typography as="span" variant="text-s">
                        $
                        {chart2Data.datasets[0].data[
                          chart2Data.datasets[0].data.length - 3 - getArrayPosition(chart2Data, "Fuel")
                        ].toFixed(2)}
                      </Typography>
                    </HoverState>
                  </CategoryButton>
                  <CategoryButton
                    onClick={() =>
                      setSidebar({
                        active: true,
                        type: "Public Transport",
                      })
                    }
                    style={{
                      left: isMobile ? `${getArrayPosition(chart2Data, "Public Transport") * 50}px` : "auto",
                      top: isMobile ? "auto" : `${getArrayPosition(chart2Data, "Public Transport") * 50}px`,
                      background: getAffordabilityColor(getArrayPosition(chart2Data, "Public Transport"), true),
                    }}
                    onMouseOver={() => setHover(getArrayPosition(chart2Data, "Public Transport") + 1)}
                    onMouseOut={() => setHover(null)}
                  >
                    {PUBLIC_TRANSPORT}

                    <HoverState>
                      <Typography as="span" variant="label-s">
                        Public Transport
                      </Typography>
                      <Typography as="span" variant="text-s">
                        $
                        {chart2Data.datasets[0].data[
                          chart2Data.datasets[0].data.length - 3 - getArrayPosition(chart2Data, "Public Transport")
                        ].toFixed(2)}
                      </Typography>
                    </HoverState>
                  </CategoryButton>
                  <CategoryButton
                    onClick={() =>
                      setSidebar({
                        active: true,
                        type: "Tolls",
                      })
                    }
                    style={{
                      left: isMobile ? `${getArrayPosition(chart2Data, "Tolls") * 50}px` : "auto",
                      top: isMobile ? "auto" : `${getArrayPosition(chart2Data, "Tolls") * 50}px`,
                      background: getAffordabilityColor(getArrayPosition(chart2Data, "Tolls"), true),
                    }}
                    onMouseOver={() => setHover(getArrayPosition(chart2Data, "Tolls") + 1)}
                    onMouseOut={() => setHover(null)}
                  >
                    {TOLLS}

                    <HoverState>
                      <Typography as="span" variant="label-s">
                        Tolls
                      </Typography>
                      <Typography as="span" variant="text-s">
                        $
                        {chart2Data.datasets[0].data[
                          chart2Data.datasets[0].data.length - 3 - getArrayPosition(chart2Data, "Tolls")
                        ].toFixed(2)}
                      </Typography>
                    </HoverState>
                  </CategoryButton>
                  <CategoryButton
                    onClick={() =>
                      setSidebar({
                        active: true,
                        type: "Roadside Assist",
                      })
                    }
                    style={{
                      left: isMobile ? `${getArrayPosition(chart2Data, "Roadside Assist") * 50}px` : "auto",
                      top: isMobile ? "auto" : `${getArrayPosition(chart2Data, "Roadside Assist") * 50}px`,
                      background: getAffordabilityColor(getArrayPosition(chart2Data, "Roadside Assist"), true),
                    }}
                    onMouseOver={() => setHover(getArrayPosition(chart2Data, "Roadside Assist") + 1)}
                    onMouseOut={() => setHover(null)}
                  >
                    {ROADSIDE_ASSIST}

                    <HoverState>
                      <Typography as="span" variant="label-s">
                        Roadside Assist
                      </Typography>
                      <Typography as="span" variant="text-s">
                        $
                        {chart2Data.datasets[0].data[
                          chart2Data.datasets[0].data.length - 3 - getArrayPosition(chart2Data, "Roadside Assist")
                        ].toFixed(2)}
                      </Typography>
                    </HoverState>
                  </CategoryButton>
                </CategoriesContainer>
              </Scroll>
            )}
          </div>

          <div className="col-span-12">
            <TimelineSelection>
              <PlayTimeline play={play}>
                {!hydrating && quarters && (
                  <button
                    onClick={() => {
                      if (!play && currentPeriod === quarters.length - 1) setCurrentPeriod(0)
                      setPlay(!play)
                    }}
                  >
                    <i />
                    <span>See the change over time</span>
                  </button>
                )}
              </PlayTimeline>
              {!hydrating && quarters && (
                <Timeline>
                  <ul>
                    {quarters.map((period, i) => {
                      return (
                        <li key={i}>
                          {period === "Q3 2024" &&
                            taTooltip.data?.siteSettings?.themeSiteSettings?.transportAffordabilityTooltip && (
                              <i>
                                <a className="tt-methodology">{INFO}</a>
                                <Tooltip anchorSelect=".tt-methodology" style={{ width: "250px", zIndex: 50 }}>
                                  {taTooltip.data.siteSettings.themeSiteSettings.transportAffordabilityTooltip}
                                </Tooltip>
                              </i>
                            )}
                          <TickDate
                            className={
                              i === currentPeriod
                                ? "opacity-100"
                                : i === 0
                                  ? currentPeriod === 1
                                    ? "opacity-0"
                                    : "opacity-50"
                                  : "opacity-0"
                            }
                          >
                            {period}
                          </TickDate>
                        </li>
                      )
                    })}
                  </ul>
                  <input
                    type="range"
                    min="0"
                    max={quarters.length - 1}
                    step={1}
                    value={currentPeriod}
                    list="period-marks"
                    onChange={(e) => setCurrentPeriod(parseInt(e.target.value))}
                  />
                </Timeline>
              )}
            </TimelineSelection>
          </div>
        </div>
      </HeaderContainer>

      <AnimatePresence>
        {sidebar.active && quarters && rawChartData && (
          <Sidebar
            sidebar={sidebar}
            setSidebar={setSidebar}
            quarter={quarters[quarters.length - 1]}
            previousQuarter={quarters[quarters.length - 2]}
            data={rawChartData}
            timeframe={timeframe}
          />
        )}
      </AnimatePresence>
    </>
  )
})

const Sidebar = ({
  sidebar,
  setSidebar,
  quarter,
  previousQuarter,
  data,
  timeframe,
}: {
  sidebar: {
    active: boolean
    type?: keyof typeof iconMap | undefined
  }
  setSidebar: Dispatch<
    SetStateAction<{
      active: boolean
      type?: keyof typeof iconMap | undefined
    }>
  >
  quarter: string
  previousQuarter: string
  data: ChartDataT[]
  timeframe: "weekly" | "yearly"
}) => {
  const currentData = useMemo(() => {
    return data.map((d) => ({
      location: d.location,
      taxonomy: d.taxonomy,
      state: d.state,
      data: d.data[d.data.length - 1].filter((t) => t.type === sidebar.type)[0].cost,
      total: d.data[d.data.length - 1].filter((t) => t.type !== "Share of Income").reduce((a, b) => a + b.cost, 0),
    }))
  }, [])

  const previousData = useMemo(() => {
    return data.map((d) => ({
      location: d.location,
      taxonomy: d.taxonomy,
      state: d.state,
      data: d.data[d.data.length - 2].filter((t) => t.type === sidebar.type)[0].cost,
      total: d.data[d.data.length - 2].filter((t) => t.type !== "Share of Income").reduce((a, b) => a + b.cost, 0),
    }))
  }, [])

  const highLow = useMemo(() => {
    if (currentData && previousData) {
      const sorted = currentData.filter((data) => data.taxonomy === "City").sort((a, b) => a.data - b.data)

      return {
        high: sorted[sorted.length - 1],
        low: sorted[0],
        prevHigh: previousData.filter((data) => data.location === sorted[sorted.length - 1].location)[0],
        prevLow: previousData.filter((data) => data.location === sorted[0].location)[0],
      }
    }
  }, [currentData, previousData])

  return (
    <SidebarContainer
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{
        type: "keyframes",
      }}
    >
      <button onClick={() => setSidebar({ active: false })}>{CLOSE}</button>

      <SidebarContent>
        <SidebarHeader>
          {iconMap[sidebar.type!]}
          <div>
            <Typography as="h4">{sidebar.type}</Typography>
            <Typography as="span">Per household ({quarter})</Typography>
          </div>
        </SidebarHeader>

        <div>
          <Table cellPadding={0} cellSpacing={0}>
            <thead>
              <tr>
                <th className="total">Average</th>
                <th className="total">Average</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <TableDetail>
                    <>{IconsMap["NATIONAL"]}</>
                    <Typography as="span" variant="h4">
                      ${numberWithCommas(currentData.reduce((a, b) => a + b.data, 0) / 15, true)}
                    </Typography>
                    <Difference
                      current={currentData.reduce((a, b) => a + b.data, 0) / 15}
                      previous={previousData.reduce((a, b) => a + b.data, 0) / 15}
                      previousQuarter={previousQuarter}
                      timeframe={timeframe}
                    />
                  </TableDetail>
                </td>
                <td>
                  <TableDetail>
                    <SmallPie>
                      <Chart
                        type="doughnut"
                        data={{
                          datasets: [
                            {
                              data: [
                                round(
                                  (currentData.reduce((a, b) => a + b.data, 0) /
                                    currentData.reduce((a, b) => a + b.total, 0)) *
                                    100,
                                  1,
                                ),
                                round(
                                  100 -
                                    (currentData.reduce((a, b) => a + b.data, 0) /
                                      currentData.reduce((a, b) => a + b.total, 0)) *
                                      100,
                                  1,
                                ),
                              ],
                              backgroundColor: ["#5E5E5E", "#D5D5D5"],
                            },
                          ],
                        }}
                        options={{
                          cutout: "70%",

                          plugins: {
                            tooltip: {
                              enabled: false,
                            },

                            legend: {
                              display: false,
                            },
                          },
                        }}
                      />
                      <span>
                        {round(
                          (currentData.reduce((a, b) => a + b.data, 0) / currentData.reduce((a, b) => a + b.total, 0)) *
                            100,
                          1,
                        )}
                        %
                      </span>
                    </SmallPie>

                    <Typography as="span" variant="label-m">
                      of weekly transport costs
                    </Typography>
                  </TableDetail>
                </td>
              </tr>
            </tbody>
          </Table>
        </div>

        {highLow && (
          <div>
            <Typography as="div" variant="label-l">
              Capital Cities
            </Typography>
            <Table cellPadding={0} cellSpacing={0} className="mt-4">
              <thead>
                <tr>
                  <th className="high">Highest</th>
                  <th className="low">Lowest</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <TableDetail iconColor={getColor(highLow.high.state, false)}>
                      <>{IconsMap[highLow.high.state as keyof typeof IconsMap]}</>
                      <Typography as="label" variant="label-s">
                        {highLow.high.location}
                      </Typography>
                      <Typography as="span" variant="h4">
                        ${numberWithCommas(highLow.high.data, true)}
                      </Typography>
                      <Difference
                        current={highLow.high.data}
                        previous={highLow.prevHigh.data}
                        previousQuarter={previousQuarter}
                        timeframe={timeframe}
                      />
                    </TableDetail>
                  </td>
                  <td>
                    <TableDetail iconColor={getColor(highLow.low.state, false)}>
                      <>{IconsMap[highLow.low.state as keyof typeof IconsMap]}</>
                      <Typography as="label" variant="label-s">
                        {highLow.low.location}
                      </Typography>
                      <Typography as="span" variant="h4">
                        ${numberWithCommas(highLow.low.data, true)}
                      </Typography>
                      <Difference
                        current={highLow.low.data}
                        previous={highLow.prevLow.data}
                        previousQuarter={previousQuarter}
                        timeframe={timeframe}
                      />
                    </TableDetail>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}

        <Button
          color="yellow"
          href="#categories"
          size="small"
          icon="ARROW_DOWN"
          iconStyle="right"
          onClick={() => setSidebar({ active: false })}
        >
          See more
        </Button>
      </SidebarContent>
    </SidebarContainer>
  )
}

const SmallPie = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-[45%] relative mb-2",
      "[&_span]:absolute [&_span]:top-1/2 [&_span]:block [&_span]:w-full [&_span]:text-center [&_span]:left-0 [&_span]:[transform:translateY(-70%)]",
      className,
    )}
    {...props}
  />
))
SmallPie.displayName = "SmallPie"

const SidebarContainer = forwardRef<HTMLDivElement, ComponentProps<typeof motion.div>>(
  ({ className, ...props }, ref) => (
    <motion.div
      ref={ref}
      className={cn(
        "w-full bg-white h-full fixed top-0 right-0 z-10 text-black",
        "px-4 pb-4 pt-[calc(100px+var(--spacing-4))]",
        "shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]",
        "[&_button]:w-[20px] [&_button]:h-[20px] [&_button]:absolute [&_button]:top-[calc(100px+var(--spacing-4))] [&_button]:right-4",
        "[&_button_svg]:w-full [&_button_svg]:h-full",
        "md:max-w-[500px]",
        className,
      )}
      {...props}
    />
  ),
)
SidebarContainer.displayName = "SidebarContainer"

const SidebarContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("w-full flex flex-col py-4 gap-4", "[&_h4]:flex [&_h4]:items-center", "[&>a]:self-start", className)}
    {...props}
  />
))
SidebarContent.displayName = "SidebarContent"

const SidebarHeader = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex gap-4 w-full",
      "[&>div]:flex [&>div]:flex-col",
      "[&>div>span]:type-body-xs [&>div>span]:text-mid-grey",
      className,
    )}
    {...props}
  />
))
SidebarHeader.displayName = "SidebarHeader"

const Table = forwardRef<HTMLTableElement, ComponentPropsWithoutRef<"table">>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={cn(
      "w-full my-7 border-collapse table-fixed",
      // Tailwind's class scanner doesn't reliably pick up a bare `,` inside
      // an arbitrary variant (confirmed: `[&_tr>th,&_tr>td]:...` silently
      // emits no CSS), so `th`/`td` are targeted with separate declarations
      // rather than the comma-combined selector.
      "[&_tr>th]:p-3 [&_tr>th]:border [&_tr>th]:border-grey [&_tr>th]:type-label-m [&_tr>th]:font-bold",
      "[&_tr>td]:p-3 [&_tr>td]:border [&_tr>td]:border-grey [&_tr>td]:type-label-m [&_tr>td]:font-bold",
      "[&_th.total]:text-mid-grey [&_th.total]:bg-neutral-light",
      "[&_th.high]:text-red [&_th.high]:bg-red-light",
      "[&_th.low]:text-green [&_th.low]:bg-green-light",
      "[&_td]:align-top",
      className,
    )}
    {...props}
  />
))
Table.displayName = "Table"

// `iconColor` covers the two call sites that override the default `svg`
// color per-instance (highLow.high/low state colors) — same CSS-variable
// technique as blocks/data/transport-affordability-categories.tsx's
// `TableDetail`.
const TableDetail = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div"> & { iconColor?: string }>(
  ({ className, iconColor, style, ...props }, ref) => (
    <div
      ref={ref}
      style={iconColor ? { ...style, ["--icon-color" as any]: iconColor } : style}
      className={cn(
        "flex flex-col items-center",
        "[&_svg]:text-mid-grey [&_svg]:h-[70px]",
        "[&_span]:my-1 [&_span]:text-center",
        iconColor && "[&_svg]:!text-[var(--icon-color)]",
        className,
      )}
      {...props}
    />
  ),
)
TableDetail.displayName = "TableDetail"

const HeaderContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Container>>(
  ({ className, ...props }, ref) => (
    <Container
      ref={ref}
      className={cn(
        "bg-black py-6 w-full text-white relative",
        "[&_select]:type-label-s [&_select_option]:type-body-s",
        className,
      )}
      {...props}
    />
  ),
)
HeaderContainer.displayName = "HeaderContainer"

const StyledToggleContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof ToggleContainer>>(
  ({ className, ...props }, ref) => (
    <ToggleContainer ref={ref} className={cn("justify-center", className)} {...props} />
  ),
)
StyledToggleContainer.displayName = "StyledToggleContainer"

const ChartInstance = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-full h-0 pt-[80%] relative overflow-hidden",
      "[&_canvas]:absolute [&_canvas]:top-0 [&_canvas]:left-0",
      className,
    )}
    {...props}
  />
))
ChartInstance.displayName = "ChartInstance"

const Scroll = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("w-full overflow-x-auto md:overflow-visible", className)} {...props} />
))
Scroll.displayName = "Scroll"

const CategoriesContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "relative block float-left my-3 h-[50px] w-[calc(50px*8)]",
        "md:h-[calc(50px*8)] md:w-full",
        className,
      )}
      {...props}
    />
  ),
)
CategoriesContainer.displayName = "CategoriesContainer"

// Per-instance `left`/`top`/`background` (position along the row on mobile
// vs. down the column at `md:`+, and the affordability color) are supplied
// via `style` at each call site using the `isMobile` flag — the same
// breakpoint stitches' `@tablet` mapped to (useIsMobile() = width < 900).
const CategoryButton = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<"button">>(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "w-[40px] h-[40px] rounded-[20px] p-2 box-border bg-dark-grey text-black absolute left-0 z-[5]",
        "transition-all duration-[350ms] ease-movement",
        "[&_svg]:w-full [&_svg]:h-auto",
        "md:left-auto md:right-0 md:[&:hover>div]:opacity-100",
        className,
      )}
      {...props}
    />
  ),
)
CategoryButton.displayName = "CategoryButton"

const HoverState = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "absolute p-1 rounded-[6px] bg-black top-0 right-full [transform:translateX(-8px)] whitespace-nowrap text-white",
      "flex flex-col items-end",
      "opacity-0 pointer-events-none transition-all duration-[350ms] ease-movement",
      className,
    )}
    {...props}
  />
))
HoverState.displayName = "HoverState"

const Selector = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex justify-center items-center flex-col py-4",
      "[&_select]:mt-1 [&_select]:border-none [&_select]:bg-black [&_select]:type-body-s [&_select]:text-white [&_select]:px-3 [&_select]:cursor-pointer [&_select]:appearance-none",
      // Base64-encoded chevron with white stroke — expressed as the CSS
      // `background` shorthand (image/repeat/position/size) so the four
      // `!important`s the original rule needed collapse into one.
      "[&_select]:![background:url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMCA2Ij4gPHBhdGggZD0iTTEgMWw0IDQgNC00IiBmaWxsPSJub25lIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjIiIC8+PC9zdmc+')_no-repeat_right_10px_center/10px_6px]",
      "[&_select]:!pr-[30px]",
      "[&_select:focus]:text-white [&_select:focus]:outline-none [&_select:hover]:text-white [&_select:hover]:outline-none",
      className,
    )}
    {...props}
  />
))
Selector.displayName = "Selector"

const MapIcon = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("mt-4 flex items-center justify-center w-full h-[50px]", className)} {...props} />
))
MapIcon.displayName = "MapIcon"
